import React, { useState, useEffect, useMemo } from "react";
import { notificarError, notificarMsg } from "../Almacenes/AlmacenNotify";
import ProductFinder from "./ProductFinder";
import IngresoTable from "./IngresoTable";
import moment from "moment";
import { GUIA_REMISION, NOTA_INGRESO, PRECIO_MENOR } from "../../Global";
import { TIPOS_DOCUMENTO_ASOCIADO_INGRESOS } from "./constants";
import Modal from '../MovimientosAlmacen/MovimientosModal.js';
import ListaOC from '../Compras/OrdenCompraListSearch';
export default function EditarIngreso({ idMovimiento, onActualizacionCompletada, onClose }) {
    const [proveedor, setProveedor] = useState(null);
    const [items, setItems] = useState([]);
    const [linkedDocument, setLinkedDocument] = useState(null);
    const [confEmpresa, setConfEmpresa] = useState({
        movAlmacenJustPrecioVenta: false
    });
    const [permisos, setPermisos] = useState({
        modificarPrecioCosto: false,
        modificarPrecioVenta: false
    });
    const [loading, setLoading] = useState(true);
    const [datosMovimiento, setDatosMovimiento] = useState(null);
    const [compraExterna, setCompraExterna] = useState(null);
    const [tiposTransaccion, setTiposTransaccion] = useState([]);
    const [showTotal, setShowTotal] = useState(false);
    const [botonBloqueado, setBotonBloqueado] = useState(false);
    const [tiempoBloqueo, setTiempoBloqueo] = useState(null);
    const [sucursal, setSucursal] = useState({ Abastecimiento: false });
    const tiposDocumentoAsociado = TIPOS_DOCUMENTO_ASOCIADO_INGRESOS;

    const puedeModificarPrecioCosto = useMemo(() => {
        return permisos.modificarPrecioCosto;
    }, [permisos]);

    const puedeModificarPrecioVenta = useMemo(() => {
        return permisos.modificarPrecioVenta;
    }, [permisos]);

    const calcularTotalActual = () => {
        return items.filter(item => item.idTipoTransaccion !== 7).reduce((acc, item) => acc + Number(item.Total || 0), 0);
    };

    const calcularDiferencia = () => {
        const totalMovimiento = Number(datosMovimiento?.Total || 0);
        const totalActual = calcularTotalActual();
        return totalMovimiento - totalActual;
    };

    const hayDiferencia = () => {
        if ([GUIA_REMISION, NOTA_INGRESO].includes(+datosMovimiento.IdTipoDocumentoSunat)) {
            return false;
        }

        const totalMovimiento = Number(datosMovimiento?.Total || 0);
        const totalActual = calcularTotalActual();
        return items.length > 0 && totalActual.toFixed(2) !== totalMovimiento.toFixed(2);
    };

    const bloquearBotonTemporalmente = () => {
        setBotonBloqueado(true);
        
        if (tiempoBloqueo) {
            clearTimeout(tiempoBloqueo);
        }
        
        const nuevoTimeout = setTimeout(() => {
            setBotonBloqueado(false);
            setTiempoBloqueo(null);
        }, 2000);
        
        setTiempoBloqueo(nuevoTimeout);
    };

    useEffect(() => {
        fetchConfEmpresa();
        fetchPermisos();
    }, []);

    useEffect(() => {
        if (idMovimiento) {
            cargarDatosMovimiento();
        }
    }, [idMovimiento]);

    useEffect(() => {
        return () => {
            if (tiempoBloqueo) {
                clearTimeout(tiempoBloqueo);
            }
        };
    }, [tiempoBloqueo]);

    useEffect(() => {
        const task = async () => {
            await fetchSucursal();

            const tiposTransaccion = await fetchTiposTransaccion();

            if (items.length == 0) {
                const presentaciones = await loadProductosAsociados(datosMovimiento.IdProveedor);

                const promises = presentaciones.filter(p => !!p).map(p => chooseProductoByIdPresentacionAndNomProducto(p.IdPresentacion, p.NombreProd));
                const productos = await Promise.all(promises);

                productos.forEach(p => {
                    handleChooseProduct({
                        ...p,
                        Cantidad: 0,
                        idTipoTransaccion: tiposTransaccion[0].IdTipoTransaccionAlmacen,
                    });
                });
            }

            if (!!datosMovimiento.IdCompraExterna) {
                await cargarCompraExterna()
            }
        }

        if (datosMovimiento) {
            task()

            setShowTotal(tiposDocumentoAsociado.find(t => t.value === +datosMovimiento.tipoDoc)?.typeTotal);
        }
    }, [datosMovimiento])

    const cargarItemsNotaEgreso = async (movimientoAlmacen) => {
        if (!movimientoAlmacen.IdNotaEgreso) {
            return [];
        }

        const response = await fetch(`/api/movimientosalmacen/detallemovimiento/list/${movimientoAlmacen.IdNotaEgreso}`);
        const data = await response.json();
        return data[1];
    }

    const fetchPermisos = async () => {
        const response = await fetch(`/api/movimientosalmacen/permisos`);
        const data = await response.json();
        setPermisos(data);
    }

    const fetchConfEmpresa = async () => {
        const req = await fetch(`/api/config/emp`);
        const conf = await req.json();

        if (!('movAlmacenJustPrecioVenta' in conf)) {
            conf['movAlmacenJustPrecioVenta'] = false;
        };

        setConfEmpresa(conf);
    }

    const fetchSucursal = async () => {
        const response = await fetch(`/api/configFactElectronica/SucursalEmpresa`);

        if (!response.ok) {
            return;
        }

        const data = await response.json();

        const sucursal = data.find(s => s.IdSucursal === datosMovimiento.IdAlmacen);

        setSucursal(sucursal);
        return sucursal;
    }

    const cargarCompraExterna = async () => {
        const response = await fetch(`/api/comprasexternas/items/${datosMovimiento.IdCompraExterna}`);
        const data = await response.json();
        setCompraExterna(data[1][0]);
    }

    const cargarDatosMovimiento = async () => {
        try {
            setLoading(true);

            const response = await fetch(`/api/movimientosalmacen/item/${idMovimiento}`);

            if (!response.ok) {
                throw new Error('Error al cargar los datos del movimiento');
            }

            const responseData = await response.json();
            const data = Array.isArray(responseData) && responseData.length > 1 && Array.isArray(responseData[1]) && responseData[1].length > 0
                ? responseData[1][0]
                : {};

            if (data.IdProveedor) {
                const proveedorResponse = await fetch(`/api/proveedores/item/${data.IdProveedor}`);
                if (proveedorResponse.ok) {
                    const proveedorData = await proveedorResponse.json();
                    setProveedor(proveedorData[0][0]);
                }
            }

            if (data.NroDocumentoSunat) {
                const [serie, correlativo] = data.NroDocumentoSunat.split('-');
                setLinkedDocument({
                    serie: serie || '',
                    correlativo: correlativo || '',
                    tipoDoc: data.tipoDoc || '',
                    total: data.total || 0,
                    fechaMovimiento: data.FechaMovimiento,
                    fechaEmision: data.FechaMovimiento
                });
            }

            const items = await loadItems(data)
            setItems(items);
            setDatosMovimiento(data);
        } catch (error) {
            console.error('Error al cargar datos:', error);
            notificarError('Error al cargar los datos del movimiento: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const loadProductosAsociados = async (IdProveedor) => {
        const response = await fetch(`/api/movimientosalmacen/productos-asociados/${IdProveedor}`);
        const productos = await response.json();

        const promises = productos.map(async (producto) => {
            const req = await fetch(`/api/presentaciones/${producto.IdProducto}`);

            if (!req.ok) {
                return null;
            }

            const res = await req.json();

            if (res.respuesta.length == 0) {
                return null;
            }

            const principal = res.respuesta.find(p => p.NombrePres == "PRINCIPAL");

            if (!principal) {
                return null;
            }

            return principal;
        })

        const principals = await Promise.all(promises);

        return principals;
    }

    const chooseProductoByIdPresentacionAndNomProducto = async (idPresentacion, nomProducto) => {
        const req = await fetch(`/api/ordenescompra/listprod/382?tipoDoc=1&search=${nomProducto}`);

        if (!req.ok) {
            return null;
        }

        const data = await req.json();

        if (data.length == 0) {
            return null;
        }

        const producto = data.find(p => p.IdPresentacion === idPresentacion);
        return producto;
    }

    const loadItems = async (datosMovimiento = null) => {
        const response = await fetch(`/api/movimientosalmacen/detallemovimiento/list/${idMovimiento}`);

        if (!response.ok) {
            return [];
        }

        const responseData = await response.json();

        const initialItems = responseData[1];

        let items = [];

        if (initialItems.length == 0 && datosMovimiento) {
            items = await cargarItemsNotaEgreso(datosMovimiento);
        } else {
            items = initialItems;
        }

        const detalles = [];

        for (let i = 0; i < items.length; i++) {
            const x = items[i];

            if (x.PrecioVenta == null) {
                const precioVenta = await fetchPrecioVenta(x.IdPresentacion);
                x.PrecioVenta = precioVenta;
            }

            const cantidad = x.Cantidad || 0;
            const precioCosto = x.Precio || 0;
            const precioVenta = x.PrecioVenta || 0;
            
            const precio = confEmpresa.movAlmacenJustPrecioVenta ? precioVenta : precioCosto;
            
            const d = {
                ...x,
                label: x.NomProducto,
                IdStock: x.IdStock,
                precioCostoIgv: precioCosto,
                PrecioCosto: precioCosto,
                precioUnidadDolar: 0,
                precioUnidadDolarSinIgv: 0,
                Total: Number((cantidad * precio).toFixed(2)),
            }

            detalles.push(d);
        }

        return detalles;
    }

    const handleValueChange = (item, fieldName, newValue) => {
        console.log(`Cambio en ${fieldName}:`, { item, newValue });

        setItems(prevState => prevState.map(row => {
            if (row.IdPresentacion === item.IdPresentacion && row.idTipoTransaccion === item.idTipoTransaccion) {
                const updatedRow = { ...row, [fieldName]: newValue };

                if (fieldName === 'Cantidad' || fieldName === 'PrecioCosto' || fieldName === 'PrecioVenta') {
                    const cantidad = fieldName === 'Cantidad' ? Number(newValue) : Number(updatedRow.Cantidad || 0);
                    const precioCosto = fieldName === 'PrecioCosto' ? Number(newValue) : Number(updatedRow.PrecioCosto || 0);
                    const precioVenta = fieldName === 'PrecioVenta' ? Number(newValue) : Number(updatedRow.PrecioVenta || 0);
                    const precio = confEmpresa.movAlmacenJustPrecioVenta ? precioVenta : precioCosto;
                    updatedRow.Total = Number((cantidad * precio).toFixed(2));
                }
                
                if (fieldName === 'PrecioCosto' || fieldName === 'PrecioVenta') {
                    const precioCosto = fieldName === 'PrecioCosto' ? Number(newValue) : Number(updatedRow.PrecioCosto || 0);
                    const precioVenta = fieldName === 'PrecioVenta' ? Number(newValue) : Number(updatedRow.PrecioVenta || 0);
                    updatedRow.Porcentaje = precioCosto && precioCosto !== 0
                        ? Number((((precioVenta - precioCosto) / precioCosto) * 100).toFixed(2))
                        : 0;
                }
                
                return updatedRow;
            }
            return row;
        }));

        bloquearBotonTemporalmente();

        notificarMsg(`${fieldName} actualizado a ${newValue} para ${item.label}`);
    }

    const handleChooseProduct = async (product) => {
        const costos = await fetchCostos(product.IdPresentacion);
        const precioVenta = await fetchPrecioVenta(product.IdPresentacion);

        setItems(prevState => {
            const cantidadSeleccionados = prevState.filter(item => item.IdPresentacion === product.IdPresentacion).length;

            if (cantidadSeleccionados >= 2) {
                notificarError("El producto solo puede ser seleccionado como máximo dos veces.");
                return prevState;
            }

            const cantidad = 'Cantidad' in product ? product.Cantidad : 1;
            const precioCosto = product.precioCostoIgv || 0;
            
            const precio = confEmpresa.movAlmacenJustPrecioVenta ? precioVenta : precioCosto;
            
            const newItem = {
                ...product,
                Cantidad: cantidad,
                PrecioVenta: precioVenta,
                PrecioCosto: precioCosto,
                Costos: costos,
                Total: Number((cantidad * precio).toFixed(2)),
            };

            newItem.Porcentaje = newItem.PrecioCosto && newItem.PrecioCosto !== 0
                ? Number((((newItem.PrecioVenta - newItem.PrecioCosto) / newItem.PrecioCosto) * 100).toFixed(2))
                : 0;

            return [...prevState, newItem];
        });

        bloquearBotonTemporalmente();
    }

    const fetchTiposTransaccion = async () => {
        const response = await fetch(`/api/movimientosalmacen/tipotransaccion/list/${datosMovimiento.IdTipoDocumentoSunat}?idTipoMovAlmacen=1`);
        const data = await response.json();
        setTiposTransaccion(data[1]);
        return data[1];
    }

    const fetchCostos = async (IdPresentacion) => {
        try {
            const response = await fetch(`/api/movimientosAlmacen/costo-historico/${IdPresentacion}`);

            if (!response.ok) {
                return [{ Precio: 0 }, { Precio: 0 }, { Precio: 0 }];
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error al obtener costos:', error);
            return [{ Precio: 0 }, { Precio: 0 }, { Precio: 0 }];
        }
    };

    const fetchPrecioVenta = async (IdPresentacion) => {
        const response = await fetch(`/api/presentaciones/precios/${IdPresentacion}`);

        if (!response.ok) {
            return 0;
        }

        const data = await response.json();
        const precios = data.respuesta;

        return precios.find(precio => precio.IdPrecioPlantilla === PRECIO_MENOR).PrecioIGV;
    }

    const deleteItem = async (item, showAlert = true) => {
        try {
            const response = await fetch(`/api/movimientosalmacen/detallemovimiento/delete/${item.IdDetalleMovimientosAlmacen}`, {
                method: 'DELETE',
            });

            if (!response.ok) {
                throw new Error('Error al eliminar el item');
            }

            if (showAlert) {
                notificarMsg(`Item ${item.label} eliminado correctamente`);
            }
        } catch (error) {
            console.error(error);

            if (showAlert) {
                notificarError(`Surgió un error al eliminar el item ${item.label}`);
            }
        }
    }

    const handleDelete = (item) => {
        const presentacion = item.IdPresentacion;
        const tipoTransaccion = item.idTipoTransaccion;
        const toDeleteIndex = items.findIndex(item => item.IdPresentacion == presentacion && item.idTipoTransaccion == tipoTransaccion);

        const newItems = [...items];
        newItems.splice(toDeleteIndex, 1);
        setItems(newItems);
    }

    const saveItem = async (item) => {
        const req = await fetch('/api/movimientosalmacen/detallemovimiento/add/', {
            method: 'POST',
            body: JSON.stringify({
                IdDetalleMovimientosAlmacen: 1,
                Cantidad: item.Cantidad,
                FechaCaducidad: null,
                IdMovimientoAlmacen: idMovimiento,
                IdProducto: item.IdProducto,
                IdLote: null,
                IdPresentacion: item.IdPresentacion,
                IdUnidad: item.IdUnidad,
                IdStock: null,
                Precio: item.PrecioCosto,
                PrecioVenta: item.PrecioVenta,
                precioUnidadDolar: 0,
                precioUnidadDolarSinIgv: 0,
                Total: item.Cantidad * (confEmpresa.movAlmacenJustPrecioVenta ? item.PrecioVenta : item.PrecioCosto),
                idTipoTransaccion: item.idTipoTransaccion,
                Flete: 0,
                FechaVencimiento: item.FechaVencimiento,
                CodLote: item.CodLote,
            }),
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })

        if (!req.ok) {
            notificarError(`Surgió un error al registrar el item ${item.label}`);
        }

        notificarMsg(`Item ${item.label} registrado correctamente`);
    }

    const handleGuardarCambios = async () => {
        if (hayDiferencia()) {
            const totalMovimiento = Number(datosMovimiento?.Total || 0);
            const totalActual = calcularTotalActual();
            const diferencia = Math.abs(totalMovimiento - totalActual);
            
            notificarError(
                `No se puede guardar. Los totales no coinciden:\n` +
                `Total del movimiento: S/ ${totalMovimiento.toFixed(2)}\n` +
                `Total actual: S/ ${totalActual.toFixed(2)}\n` +
                `Diferencia: S/ ${diferencia.toFixed(2)}`
            );
            return;
        }

        if (!!sucursal?.Abastecimiento) {
            const itemsConCantidad = items.filter(item => item.Cantidad > 0);
            const erroresValidacion = [];

            for (const item of itemsConCantidad) {
                const erroresItem = [];

                if (!item.CodLote || item.CodLote.trim() === '') {
                    erroresItem.push('Código de Lote es requerido');
                }


                if (!item.FechaVencimiento || !(item.FechaVencimiento instanceof Date) || isNaN(item.FechaVencimiento.getTime())) {
                    erroresItem.push('Fecha de Vencimiento es requerida o no tiene un formato válido');
                } else {
                    const hoy = new Date();
                    hoy.setHours(0,0,0,0);
                    const fechaVenc = new Date(item.FechaVencimiento.getTime());
                    fechaVenc.setHours(0,0,0,0);
                    if (fechaVenc < hoy) {
                        erroresItem.push('Fecha de Vencimiento no puede ser anterior a la fecha actual');
                    }
                }


                if (erroresItem.length > 0) {
                    erroresValidacion.push(`${item.label}: ${erroresItem.join(', ')}`);
                }
            }

            if (erroresValidacion.length > 0) {
                notificarError(
                    `Los siguientes productos tienen errores:\n\n` +
                    erroresValidacion.join('\n')
                );
                return;
            }
        }

        try {
            const originaItems = await loadItems();
            
            if (originaItems.length > 0) {
                await Promise.all(originaItems.map(item => deleteItem(item, false)));
            }

            if (items.length > 0) {
                const itemsToSave = items.filter(item => item.Cantidad > 0);

                for (const item of itemsToSave) {
                    await saveItem(item);
                }
            }

            const newItems = await loadItems();
            setItems(newItems);

            notificarMsg('Cambios guardados correctamente');
        } catch (error) {
            console.error('Error al guardar cambios:', error);
            notificarError('Error al guardar los cambios');
        }

        onActualizacionCompletada();
        onClose();
    }

    if (loading) {
        return (
            <div className="container-fluid d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Cargando...</span>
                </div>
            </div>
        );
    }

    return <div className="container-fluid" style={{ width: '100%' }}>

        <nav>
            <div className="nav nav-tabs" id="nav-tab" role="tablist">
                <button className="nav-link" id="nav-info-tab" data-toggle="tab" data-target="#nav-info" type="button" role="tab" aria-controls="nav-info" aria-selected="true">Datos de movimiento</button>
                <button className="nav-link active" id="nav-items-tab" data-toggle="tab" data-target="#nav-items" type="button" role="tab" aria-controls="nav-items" aria-selected="false">Items</button>
            </div>
        </nav>

        <div className="tab-content" id="nav-tabContent">
            <div className="tab-pane fade" id="nav-info" role="tabpanel" aria-labelledby="nav-info-tab">
                <div className="row">
                    <div className="col-12">
                        <div className="form-row">
                            <div className="form-group col-12">
                                <label htmlFor="IdTipoTransaccionAlmacen"><strong>Proveedor</strong></label>
                                <input
                                    type="text"
                                    className="form-control w-100"
                                    value={proveedor.NombreComercial}
                                    disabled
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group col-12">
                                <label htmlFor="FechaMovimiento"><strong>Fecha Movimiento</strong></label>
                                <input
                                    type="text"
                                    value={moment(datosMovimiento.FechaMovimiento).format("YYYY-MM-DD")}
                                    className="form-control w-100"
                                    disabled
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group col-12">
                                <label htmlFor="FechaMovimiento"><strong>Documento asociado</strong></label>
                                <input
                                    type="text"
                                    value={datosMovimiento.NroDocumentoSunat}
                                    className="form-control w-100"
                                    disabled
                                />
                            </div>
                        </div>

                        {
                            showTotal && (
                                <div className="form-row">
                                    <div className="form-group col-12">
                                        <label htmlFor="FechaMovimiento"><strong>Total</strong></label>
                                        <input
                                            type="text"
                                            value={`S/ ${datosMovimiento.Total}`}
                                            className="form-control w-100"
                                            disabled
                                        />
                                    </div>
                                </div>
                            )
                        }

                        {
                            datosMovimiento.Glosa && (
                                <div className="form-row">
                                    <div className="form-group col-12">
                                        <label htmlFor="Glosa"><strong>Glosa</strong></label>
                                        <textarea
                                            className="form-control w-100"
                                            rows="3"
                                            value={datosMovimiento.Glosa || ''}
                                            disabled
                                        />
                                    </div>
                                </div>
                            )
                        }

                        {
                            !!compraExterna && (
                                <div className="form-row">
                                    <div className="form-group col-12">
                                        <label htmlFor="FechaMovimiento"><strong>Gastos asociados de flete</strong></label>
                                        <ul>
                                            <li>Fecha: {moment(compraExterna.FechaMovimiento).format("YYYY-MM-DD")}</li>
                                            <li>Proveedor: {compraExterna.NombreEmpresa}</li>
                                            <li>Nro. Documento: {compraExterna.NroDocumentoSunat}</li>
                                            <li>Total: S/ {compraExterna.Total}</li>
                                            <li>Glosa: {compraExterna.Glosa}</li>
                                        </ul>
                                    </div>
                                </div>
                           )
                        }
                    </div>
                </div>

                <div className="d-flex justify-content-end">
                    <button type="button" className="btn btn-secondary">Cerrar</button>
                </div>
            </div>
            <div className="tab-pane fade show active" id="nav-items" role="tabpanel" aria-labelledby="nav-items-tab">
                <div className="row mb-3">
                    <div className="col-12">
                        <ProductFinder onChoose={handleChooseProduct} tiposTransaccion={tiposTransaccion} />
                    </div>
                </div>
                {
                    ![GUIA_REMISION, NOTA_INGRESO].includes(+datosMovimiento.IdTipoDocumentoSunat) && (
                        <div className="row mb-3">
                            <div className="col-12 d-flex flex-column flex-md-row align-items-md-center">
                                <div className="mr-4 mb-2 mb-md-0">
                                    <strong>Total del movimiento de almacén:</strong>{" "}
                                    <span>
                                        S/ {Number(datosMovimiento.Total).toFixed(2)}
                                    </span>
                                </div>
                                <div className="mr-4 mb-2 mb-md-0">
                                    <strong>Total actual:</strong>{" "}
                                    <span style={{ color: hayDiferencia() ? 'red' : 'black' }}>
                                        S/ {calcularTotalActual().toFixed(2)}
                                    </span>
                                </div>
                                {hayDiferencia() && (
                                    <div>
                                        <strong>Diferencia:</strong>{" "}
                                        <span style={{ color: 'red' }}>
                                            S/ {calcularDiferencia().toFixed(2)}
                                        </span>
                                    </div>
                                )}
                            </div>
                            {
                                !!compraExterna && (
                                    <div className="col-12 mt-2">
                                        <strong>Gastos asociados de flete:</strong>{" "}
                                        <span>S/ {compraExterna.Total}</span>
                                    </div>
                                )
                            }
                        </div>
                    )
                }

                <div className="row" style={{ minHeight: '300px' }}>
                    <div className="col-12">
                        <IngresoTable
                            confEmpresa={confEmpresa}
                            sucursal={sucursal}
                            data={items}
                            tiposTransaccion={tiposTransaccion}
                            handleValueChange={handleValueChange}
                            compraExterna={compraExterna}
                            handleDelete={handleDelete}
                            puedeModificarPrecioCosto={puedeModificarPrecioCosto}
                            puedeModificarPrecioVenta={puedeModificarPrecioVenta}
                        />
                    </div>
                </div>

                <div className="d-flex justify-content-between mt-3">
                    <button type="button" className="btn btn-secondary" onClick={onClose}>Cerrar</button>
                    <button 
                        type="button" 
                        className={`btn ${botonBloqueado ? 'btn-secondary' : 'btn-info'}`}
                        onClick={handleGuardarCambios}
                        disabled={botonBloqueado}
                    >
                        Guardar cambios
                    </button>
                </div>
            </div>
        </div>
    </div>
}
